import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import AppModal from "components/shared/modal/AppModal";

import { postEnableCloudboxSnapshotSchedulation } from "redux/handlers/addonsHandle";

import { getSelectedCloudboxData } from "redux/selectors/cloudboxSelector";

import { useAppDispatch } from "hooks/reduxHook";

const ChangeSnapshotPlan = (): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();
 const cloudboxData = useSelector(getSelectedCloudboxData);

 const [open, setOpen] = useState<boolean>(false);
 const [selectedType, setSelectedType] = useState<string>("");
 const [loading, setLoading] = useState<boolean>(false);
 const [maxSnapNumber, setMaxSnapNumber] = useState<number>(1);

 const handleOpen = () => setOpen(true);
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  if (selectedType === "weekly") {
   await dispatch(
    postEnableCloudboxSnapshotSchedulation(cloudboxData?.id || 0, maxSnapNumber, 1, null)
   );
  } else if (selectedType === "monthly") {
   await dispatch(
    postEnableCloudboxSnapshotSchedulation(cloudboxData?.id || 0, maxSnapNumber, null, 1)
   );
  } else {
   await dispatch(
    postEnableCloudboxSnapshotSchedulation(cloudboxData?.id || 0, maxSnapNumber, null, null)
   );
  }
  handleClose();
  setLoading(false);
 };

 const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setSelectedType((event.target as HTMLInputElement).value);
 };

 const checkDisabled = (): boolean => {
  return loading || selectedType === "" || maxSnapNumber < 1;
 };

 const RenderMessage = (): ReactElement => {
  switch (selectedType) {
   case "daily":
    return (
     <Alert severity="info">
      <FormattedMessage id="addons.cloudbox.snapshotDailyMessage" />
     </Alert>
    );
   case "weekly":
    return (
     <Alert severity="info">
      <FormattedMessage id="addons.cloudbox.snapshotWeeklyMessage" />
     </Alert>
    );
   case "monthly":
    return (
     <Alert severity="info">
      <FormattedMessage id="addons.cloudbox.snapshotMonthlyMessage" />
     </Alert>
    );
   default:
    return <></>;
  }
 };

 return cloudboxData ? (
  <>
   <Button onClick={() => handleOpen()}>
    <FormattedMessage id="addons.cloudbox.planSnapshot" />
   </Button>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "addons.cloudbox.planSnapshot" })}
    handleClose={handleClose}
    handleConfirm={handleConfirm}
    disabled={checkDisabled()}>
    <Stack spacing={1}>
     <Alert severity="info">
      <FormattedMessage id="addons.cloudbox.planSnapshotMessage" />
     </Alert>
     <Divider />
     <Stack spacing={2}>
      <TextField
       autoComplete="off"
       value={maxSnapNumber}
       label={intl.formatMessage({ id: "addons.cloudbox.maxSnapshots" })}
       type="number"
       InputLabelProps={{ shrink: true }}
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       onChange={({ currentTarget }) => setMaxSnapNumber(Number(currentTarget.value))}
      />
      <FormControl>
       <FormLabel>Intervallo di tempo</FormLabel>
       <RadioGroup value={selectedType} onChange={handleChange} name="radio-buttons-group">
        <FormControlLabel value="daily" control={<Radio />} label="Giornaliero" />
        <FormControlLabel value="weekly" control={<Radio />} label="Settimanale" />
        <FormControlLabel value="monthly" control={<Radio />} label="Mensile" />
       </RadioGroup>
      </FormControl>
     </Stack>
     <RenderMessage />
    </Stack>
   </AppModal>
  </>
 ) : (
  <></>
 );
};

export default ChangeSnapshotPlan;
